// ============================================
// Firestore - Interfaz centralizada con sanitizaci?n
// ============================================
//
// SIEMPRE importar setDoc, updateDoc, addDoc, writeBatch desde aqu?.
// NUNCA desde 'firebase/firestore' directamente.
//
// Firestore rechaza undefined. Esta interfaz elimina undefined autom?ticamente.
// ============================================

import {
  doc,
  collection,
  collectionGroup,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  type DocumentReference,
  type CollectionReference,
  type DocumentData,
  type SetOptions,
  type UpdateData,
  type Firestore,
  setDoc as firebaseSetDoc,
  updateDoc as firebaseUpdateDoc,
  addDoc as firebaseAddDoc,
  writeBatch as firebaseWriteBatch,
  type WriteBatch
} from 'firebase/firestore'
import { db } from './firebase'

/**
 * Elimina recursivamente todos los valores undefined de un objeto.
 * Firestore no acepta undefined; esta funci?n garantiza datos v?lidos.
 */
export function sanitizeForFirestore<T>(data: T): T {
  if (data === undefined || data === null) {
    return data
  }
  if (Array.isArray(data)) {
    return data
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined) as T
  }
  if (typeof data === 'object' && data instanceof Date) {
    return data
  }
  if (typeof data === 'object' && !(data instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        result[key] = sanitizeForFirestore(value)
      }
    }
    return result as T
  }
  return data
}

/** setDoc con sanitizaci?n autom?tica */
export async function setDoc<T extends DocumentData>(
  reference: DocumentReference<T>,
  data: T,
  options?: SetOptions
): Promise<void> {
  const clean = sanitizeForFirestore(data) as T
  if (options) {
    return firebaseSetDoc(reference, clean, options)
  }
  return firebaseSetDoc(reference, clean)
}

/** updateDoc con sanitizaci?n autom?tica */
export async function updateDoc<T extends DocumentData>(
  reference: DocumentReference<T>,
  data: UpdateData<T>
): Promise<void> {
  const clean = sanitizeForFirestore(data) as UpdateData<T>
  return firebaseUpdateDoc(reference, clean)
}

/** addDoc con sanitizaci?n autom?tica */
export async function addDoc<T extends DocumentData>(
  reference: CollectionReference<T>,
  data: T
): Promise<DocumentReference<T>> {
  const clean = sanitizeForFirestore(data) as T
  return firebaseAddDoc(reference, clean)
}

/** writeBatch - usar batchSet() para sanitizar datos al hacer batch.set() */
export function writeBatch(firestore?: Firestore): WriteBatch {
  const store = firestore ?? db
  if (!store) throw new Error('Firestore not available')
  return firebaseWriteBatch(store)
}

/** Helper para batch.set() con sanitizaci?n */
export function batchSet<T extends DocumentData>(
  batch: WriteBatch,
  ref: DocumentReference<T>,
  data: T,
  options?: SetOptions
): WriteBatch {
  const clean = sanitizeForFirestore(data) as T
  batch.set(ref, clean, options ?? {})
  return batch
}

// Re-exportar operaciones de lectura (no necesitan sanitizaci?n)
export {
  doc,
  collection,
  collectionGroup,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit
}
